/**
 * @module AdapterCard
 * @package @geenius/adapters/solidjs-kobalte
 * @description Kobalte-native adapter summary card for the SolidJS library
 * variant, with a semantic fallback for non-Solid export-condition runtimes.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import { createMemo, createResource, Show } from "solid-js";
import { cn, loadKobalteCore } from "../lib/kobalte";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  class?: string;
}

function CardContent(props: AdapterCardProps): JSX.Element {
  return (
    <>
      <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-2xl shadow-sm transition-transform duration-300 group-hover:scale-105">
        {DOMAIN_ICONS[props.domain]}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center justify-between gap-2">
          <h3 class="truncate text-sm font-bold text-slate-950">
            {DOMAIN_LABELS[props.domain]}
          </h3>
          <AdapterStatusBadge status={props.status.status} />
        </div>
        <p class="mt-0.5 line-clamp-2 text-xs text-slate-500">
          {DOMAIN_DESCRIPTIONS[props.domain]}
        </p>
        <Show when={props.status.provider && props.status.provider !== "none"}>
          <div class="mt-2 flex items-center gap-2">
            <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[0.625rem] font-semibold text-slate-600">
              {props.status.provider}
            </span>
            <Show when={props.status.latency}>
              <span class="text-[0.625rem] text-slate-500">
                {props.status.latency}ms
              </span>
            </Show>
          </div>
        </Show>
        <Show when={props.status.error}>
          <p class="mt-1.5 truncate text-[0.625rem] text-red-600">
            {props.status.error}
          </p>
        </Show>
      </div>
    </>
  );
}

export function AdapterCard(props: AdapterCardProps): JSX.Element {
  const [core] = createResource(loadKobalteCore);
  const Button = createMemo(() => core()?.Button?.Root);
  const ariaLabel = createMemo(
    () => `${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`,
  );
  const className = createMemo(() =>
    cn(
      "group relative flex w-full items-start gap-4 overflow-hidden rounded-lg border border-slate-200 bg-white p-5 text-left text-slate-950 shadow-sm transition-colors duration-200",
      props.onClick
        ? "hover:border-sky-300 hover:bg-sky-50/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/30"
        : "cursor-default",
      props.class,
    ),
  );

  if (!props.onClick) {
    return (
      <article
        aria-label={ariaLabel()}
        class={className()}
        data-geenius-component="AdapterCard"
        data-geenius-variant="solidjs-kobalte"
      >
        <CardContent {...props} />
      </article>
    );
  }

  return (
    <Show
      fallback={
        <button
          aria-label={ariaLabel()}
          class={className()}
          data-geenius-component="AdapterCard"
          data-geenius-variant="solidjs-kobalte"
          onClick={props.onClick}
          type="button"
        >
          <CardContent {...props} />
        </button>
      }
      keyed
      when={Button()}
    >
      {(KobalteButton) => (
        <KobalteButton
          aria-label={ariaLabel()}
          class={className()}
          data-geenius-component="AdapterCard"
          data-geenius-variant="solidjs-kobalte"
          onClick={props.onClick}
          type="button"
        >
          <CardContent {...props} />
        </KobalteButton>
      )}
    </Show>
  );
}
